import React, { useState, useEffect, useCallback, useRef } from 'react';
import Select from 'react-select';
import './IDR.css'; // Import the redesigned CSS file

// --- Constants FIELD_TITLES, VALUE_MAPPINGS, Steps ---
const FIELD_TITLES = {
    Program: "Program",
    Modality: "Modality",
    CancerType: "Cancer Type",
    tcia_tumorLocation: "Primary Site Location",
    access: "Access",
    Collections: "Collections",
    collection_id: "Collections"
};

const CLINICAL_FIELD_TITLES = {
    'Age At Diagnosis': 'Age At Diagnosis',
    'BMI': 'BMI',
    'Country': 'Country',
    'Disease Code': 'Disease Code',
    'Ethnicity': 'Ethnicity',
    'Sex': 'Sex',
    'Pathologic Stage': 'Pathologic Stage',
    'Race': 'Race',
    'Sample Type': 'Sample Type',
    'Vital Status': 'Vital Status'
};

const VALUE_MAPPINGS = {
    Modality: {
        CT: "Computed Tomography",
        MR: "Magnetic Resonance",
        ANN: "Annotation",
        SEG: "Segmentation Storage",
        CR: "Computed Radiography",
        SM: "Slide Microscopy",
        PT: "Presentation State",
        OT: "Other",
        NM: 'Nuclear Medicine'
    },
    collection_id: {},
    Collections: {}
};

// Define the steps for the workflow
const WORKFLOW_STEPS = [
    {
        id: 'primary-filters',
        title: 'Primary Filters',
        description: 'Configure basic image metadata filters'
    },
    {
        id: 'collections',
        title: 'Collections',
        description: 'Select data collections based on filters'
    },
    {
        id: 'patients',
        title: 'Patient IDs & Clinical Filters',
        description: 'Filter patients and apply clinical criteria'
    },
    {
        id: 'series',
        title: 'Series IDs',
        description: 'Select specific image series'
    }
];

const containerStyle = {
    backgroundColor: '#e6f2ff',
    minHeight: '100vh'
};

// --- LoadingOverlay Component ---
const LoadingOverlay = ({isLoading}) => {
    if (!isLoading) return null;

    return (
        <div
            className="fixed inset-0"
            style={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(2px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100vw',
                height: '100vh',
                position: 'fixed',
                top: 0,
                left: 0,
                zIndex: 10000
            }}
        >
            <div className="text-white text-xl font-semibold">Loading...</div>
        </div>
    );
};

// --- ClinicalFilters Component ---
const ClinicalFilters = ({
    options,
    selectedFilters,
    onChange,
    loading,
    patientCount
}) => {
    // Local state to store temporary age input values
    const [tempAgeValues, setTempAgeValues] = useState({
        min: '',
        max: ''
    });

    // Initialize temp values when selectedFilters change
    useEffect(() => {
        const selectedAge = selectedFilters['Age At Diagnosis'] || [];
        setTempAgeValues({
            min: selectedAge[0] === null || selectedAge[0] === undefined ? '' : selectedAge[0].toString(),
            max: selectedAge[1] === null || selectedAge[1] === undefined ? '' : selectedAge[1].toString()
        });
    }, [selectedFilters]);

    const handleMultiSelectChange = (field, selectedOptions) => {
        onChange(field, selectedOptions ? selectedOptions.map(opt => opt.value) : []);
    };

    const handleAgeInputChange = (boundary, value) => {
        setTempAgeValues(prev => ({
            ...prev,
            [boundary]: value
        }));
    };

    const commitAgeChange = () => {
        const currentRange = selectedFilters['Age At Diagnosis'] || [];
        const minValue = tempAgeValues.min.trim() === '' ? null : parseInt(tempAgeValues.min.trim(), 10);
        const maxValue = tempAgeValues.max.trim() === '' ? null : parseInt(tempAgeValues.max.trim(), 10);

        let min = minValue;
        let max = maxValue;

        // Validate and adjust values
        if (min !== null && (isNaN(min) || min < 10)) min = 10;
        if (max !== null && (isNaN(max) || max > 90)) max = 90;

        if (min !== null && max !== null && min > max) {
            // If min is greater than max, reset the problematic value
            return; // Don't commit invalid range
        }

        // Only trigger onChange if the values actually changed
        const newRange = [min, max];
        const currentMin = currentRange.length > 0 ? currentRange[0] : null;
        const currentMax = currentRange.length > 1 ? currentRange[1] : null;

        if (min !== currentMin || max !== currentMax) {
            if (min === null && max === null) {
                onChange('Age At Diagnosis', []);
            } else {
                onChange('Age At Diagnosis', newRange);
            }
        }
    };

    const handleAgeKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.target.blur(); // This will trigger onBlur
        }
    };

    const handleBmiChange = (category, isChecked) => {
        const currentSelection = selectedFilters['BMI'] || [];
        let newSelection;
        if (isChecked) {
            newSelection = [...currentSelection, category];
        } else {
            newSelection = currentSelection.filter(item => item !== category);
        }
        onChange('BMI', newSelection);
    };

    const getSelectOptions = (fieldOptsArray) => {
       if(!Array.isArray(fieldOptsArray)) return [];
        return fieldOptsArray.map(opt => ({ value: opt, label: opt || '(empty)' })).sort((a, b) => a.label.localeCompare(b.label));
    };

    const getBmiOptions = () => {
        const bmiData = options['BMI'] || {};
        const order = ['Underweight', 'Normal weight', 'Overweight', 'Obese'];
        return order.map(category => ({
            label: category,
            value: category,
            count: bmiData[category] !== undefined ? bmiData[category] : 0
        }));
    };

    const ageData = options['Age At Diagnosis'] || { min: 10, max: 90, count: 0 };

    const dropdownFields = Object.keys(options || {})
        .filter(field => field !== 'Age At Diagnosis' && field !== 'BMI')
        .sort((a,b) => (CLINICAL_FIELD_TITLES[a] || a).localeCompare(CLINICAL_FIELD_TITLES[b] || b));

    return (
        <div className="clinical-filters-sidebar">
            <h4 className="clinical-filters-title">
                Clinical Filters ({patientCount !== undefined ? patientCount : '...'} Patients)
            </h4>

            {dropdownFields.map(field => {
                const fieldOptsArray = options[field];
                const currentSelection = selectedFilters[field] || [];
                return (
                    <div key={field} className="filter-item">
                        <label className="filter-label">
                            {CLINICAL_FIELD_TITLES[field] || field}:
                        </label>
                        <Select
                            isMulti
                            options={getSelectOptions(fieldOptsArray)}
                            value={currentSelection.map(v => ({ value: v, label: v || '(empty)' }))}
                            onChange={(selected) => handleMultiSelectChange(field, selected)}
                            isDisabled={loading || !Array.isArray(fieldOptsArray) || fieldOptsArray.length === 0}
                            closeMenuOnSelect={false}
                            placeholder={`Any ${CLINICAL_FIELD_TITLES[field] || field}`}
                            styles={{ menu: base => ({ ...base, zIndex: 9998 }) }}
                        />
                    </div>
                );
            })}

            {/* Age At Diagnosis Input */}
            <div className="filter-item">
                <label className="filter-label">
                    {CLINICAL_FIELD_TITLES['Age At Diagnosis']} (Range {ageData.min}-{ageData.max}):
                </label>
                <div className="age-input-container">
                    <input
                        type="number"
                        min={ageData.min}
                        max={ageData.max}
                        value={tempAgeValues.min}
                        onChange={(e) => handleAgeInputChange('min', e.target.value)}
                        onBlur={commitAgeChange}
                        onKeyPress={handleAgeKeyPress}
                        placeholder={`Min (${ageData.min})`}
                        className="age-input"
                        disabled={loading}
                    />
                    <span className="age-separator">-</span>
                    <input
                        type="number"
                        min={ageData.min}
                        max={ageData.max}
                        value={tempAgeValues.max}
                        onChange={(e) => handleAgeInputChange('max', e.target.value)}
                        onBlur={commitAgeChange}
                        onKeyPress={handleAgeKeyPress}
                        placeholder={`Max (${ageData.max})`}
                        className="age-input"
                        disabled={loading}
                    />
                </div>
            </div>

            {/* BMI Checkboxes */}
            <div className="filter-item">
                <label className="filter-label">
                    {CLINICAL_FIELD_TITLES['BMI']}:
                </label>
                {getBmiOptions().map(opt => (
                    <div key={opt.value} className="flex items-center mb-1">
                        <input
                            type="checkbox"
                            id={`bmi-${opt.value}`}
                            checked={(selectedFilters['BMI'] || []).includes(opt.value)}
                            onChange={(e) => handleBmiChange(opt.value, e.target.checked)}
                            className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            disabled={loading || opt.count === 0}
                        />
                        <label
                            htmlFor={`bmi-${opt.value}`}
                            className={`text-sm ${opt.count === 0 && !loading ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                            {opt.label} ({opt.count})
                        </label>
                    </div>
                ))}
            </div>
        </div>
    );
};

// --- PaginatedSection Component ---
const PaginatedSection = ({
    title,
    items = [],
    selectedItems,
    onItemSelect,
    onSelectAll,
    onSelectNone,
    itemsPerPage = 10,
    searchPlaceholder,
    multiSelect = false,
    isDisabled = false,
    showSearch = true,
    stepData = null  // Add stepData prop

}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [pageSelectionState, setPageSelectionState] = useState('none'); // 'none', 'partial', 'all'
    const [globalSelectionState, setGlobalSelectionState] = useState('none'); // 'none', 'partial', 'all'

    const filteredItems = React.useMemo(() => {
        if (!Array.isArray(items)) return [];
        return items.filter(item => {
            let searchValue = '';
            if (typeof item === 'object' && item !== null && item.SeriesInstanceUID) {
                searchValue = item.SeriesInstanceUID;
            } else if (typeof item === 'string') {
                searchValue = item;
            }
            return String(searchValue).toLowerCase().includes(searchTerm.toLowerCase());
        });
    }, [items, searchTerm]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filteredItems]);

    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
    const indexOfLastItem = indexOfFirstItem + itemsPerPage;
    const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

    const isSelected = useCallback((item) => {
        let itemId;
        if (typeof item === 'object' && item !== null && item.SeriesInstanceUID) {
            itemId = item.SeriesInstanceUID;
        } else {
            itemId = item;
        }

        if (!selectedItems) return false;

        return multiSelect
            ? Array.isArray(selectedItems) && selectedItems.includes(itemId)
            : selectedItems === itemId;
    }, [selectedItems, multiSelect]);

    // Calculate selection states
    React.useEffect(() => {
        if (multiSelect) {
            // Calculate page selection state
            if (currentItems.length > 0) {
                const selectedOnPage = currentItems.filter(isSelected).length;
                if (selectedOnPage === 0) {
                    setPageSelectionState('none');
                } else if (selectedOnPage === currentItems.length) {
                    setPageSelectionState('all');
                } else {
                    setPageSelectionState('partial');
                }
            } else {
                setPageSelectionState('none');
            }

            // Calculate global selection state
            if (filteredItems.length > 0) {
                const totalSelected = filteredItems.filter(isSelected).length;
                if (totalSelected === 0) {
                    setGlobalSelectionState('none');
                } else if (totalSelected === filteredItems.length) {
                    setGlobalSelectionState('all');
                } else {
                    setGlobalSelectionState('partial');
                }
            } else {
                setGlobalSelectionState('none');
            }
        }
    }, [filteredItems, currentPage, isSelected, multiSelect, selectedItems]);

    const handlePageChange = (newPage) => {
        const validPage = Math.min(Math.max(1, newPage), totalPages > 0 ? totalPages : 1);
        setCurrentPage(validPage);
    };

    const handleItemSelectionChange = (item) => {
        if (!isDisabled) {
            onItemSelect(item);
        }
    };

    const handleTogglePageSelection = () => {
        if (!isDisabled) {
            const visibleItemIds = currentItems.map(item =>
                typeof item === 'object' ? item.SeriesInstanceUID : item
            );
            if (pageSelectionState === 'all') {
                onSelectNone(visibleItemIds);
            } else {
                onSelectAll(visibleItemIds);
            }
        }
    };

    const handleToggleGlobalSelection = () => {
        if (!isDisabled) {
            const allItemIds = filteredItems.map(item =>
                typeof item === 'object' ? item.SeriesInstanceUID : item
            );
            if (globalSelectionState === 'all') {
                onSelectNone(allItemIds);
            } else {
                onSelectAll(allItemIds);
            }
        }
    };

    return (
        <div className="results-container">
            {/* Header with title and actions */}
            <div className="results-header">
                <h3 className="results-title">
                    {title} ({filteredItems.length})
                </h3>
                <div className="results-actions">
                    {multiSelect && (
                        <>
                            <button
                                className="btn-selection-toggle text-sm"
                                onClick={handleTogglePageSelection}
                                disabled={isDisabled || currentItems.length === 0}
                                title={pageSelectionState === 'all' ? 'Deselect current page' : 'Select current page'}
                            >
                                {pageSelectionState === 'all' ? 'Deselect Page' : 'Select Page'}
                            </button>
                            <button
                                className="btn-selection-all text-sm"
                                onClick={handleToggleGlobalSelection}
                                disabled={isDisabled || filteredItems.length === 0}
                                title={globalSelectionState === 'all' ? 'Deselect all items' : 'Select all items'}
                            >
                                {globalSelectionState === 'all' ? 'Deselect All' : 'Select All'}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Search Bar */}
            {showSearch && (
                <div className="results-search">
                    <input
                        type="text"
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full p-2 border rounded text-sm"
                        disabled={isDisabled}
                    />
                </div>
            )}

            {/* Results List */}
            <div className="results-list">
                {isDisabled && currentItems.length === 0 && (
                    <div className="p-4 text-center text-gray-400">Loading items...</div>
                )}
                {!isDisabled && filteredItems.length === 0 && (
                    <div className="p-4 text-center text-gray-500">No items match your criteria.</div>
                )}

                {/* Series Table */}
                {title === "Series IDs" && !isDisabled && filteredItems.length > 0 && (
                    <table className="w-full text-sm table-fixed">
                        <colgroup>
                            <col style={{ width: '5%' }} />
                            <col style={{ width: '24%' }} />
                            <col style={{ width: '8%' }} />
                            <col style={{ width: '8%' }} />
                            <col style={{ width: '13%' }} />
                            <col style={{ width: '12%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                        </colgroup>
                        <thead>
                            <tr className="bg-gray-100">
                                <th className="p-2 text-left">
                                    <input type="checkbox" className="opacity-0 pointer-events-none"/>
                                </th>
                                <th className="p-2 text-left truncate">Series UID</th>
                                <th className="p-2 text-left truncate">Program</th>
                                <th className="p-2 text-left truncate">Modality</th>
                                <th className="p-2 text-left truncate">Collection</th>
                                <th className="p-2 text-left truncate">PatientID</th>
                                <th className="p-2 text-left truncate">Instances</th>
                                <th className="p-2 text-left truncate">Clinical Data</th>
                                <th className="p-2 text-left truncate">Link</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map((item) => {
                                const id = item?.SeriesInstanceUID || 'N/A';
                                const program = item?.Program || 'N/A';
                                const modality = item?.Modality || 'N/A';
                                const collectionId = item?.collection_id || 'N/A';
                                const patientId = item?.PatientID || 'N/A';
                                const url = item?.url;
                                const hasClinicalData = item?.clinical_data ? true : false;

                                // Count instances for this SeriesInstanceUID
                                const fullData = stepData?.['series']?.fullData || [];
                                const instanceCount = fullData.filter(record =>
                                    record.SeriesInstanceUID === item.SeriesInstanceUID
                                ).length;

                                const truncateUID = (uid, len = 10) => {
                                    if (typeof uid !== 'string' || uid.length <= len) return uid;
                                    const half = Math.floor(len / 2);
                                    return uid.substring(0, half) + '...' + uid.substring(uid.length - (len - half));
                                };

                                // Program badge styling
                                const programBadgeClass = program === 'TCGA'
                                    ? 'bg-blue-100 text-blue-800'
                                    : program === 'CPTAC'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800';

                                return (
                                    <tr key={id} className={`hover:bg-gray-50 border-b ${isSelected(item) ? 'bg-blue-50' : ''}`}>
                                        <td className="p-2">
                                            <input
                                                type="checkbox"
                                                checked={isSelected(item)}
                                                onChange={() => handleItemSelectionChange(item)}
                                                disabled={isDisabled}
                                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                        </td>
                                        <td className="p-2 font-mono truncate" title={id}>{truncateUID(id, 12)}</td>
                                        <td className="p-2">
                                            <span className={`text-xs px-2 py-1 rounded font-medium ${programBadgeClass}`}>
                                                {program}
                                            </span>
                                        </td>
                                        <td className="p-2 truncate" title={VALUE_MAPPINGS.Modality[modality] || modality}>
                                            {VALUE_MAPPINGS.Modality[modality] || modality}
                                        </td>
                                        <td className="p-2 truncate" title={collectionId}>{collectionId}</td>
                                        <td className="p-2 truncate" title={patientId}>{patientId}</td>
                                        <td className="p-2 text-center">
                                            <span className="text-xs bg-gray-200 px-2 py-1 rounded">
                                                {instanceCount}
                                            </span>
                                        </td>
                                        <td className="p-2">
                                            {hasClinicalData ? (
                                                <span className="text-green-600 text-xs">✓ Available</span>
                                            ) : (
                                                <span className="text-gray-400 text-xs">Not found</span>
                                            )}
                                        </td>
                                        <td className="p-2">
                                            {url ? (
                                                <a
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:underline text-xs"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    View
                                                </a>
                                            ) : (
                                                <span className="text-gray-400 text-xs">N/A</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}

                {/* Generic List for Collections and Patient IDs */}
                {title !== "Series IDs" && !isDisabled && filteredItems.length > 0 && (
                    <div className="p-2">
                        {currentItems.map((item) => {
                            const id = item;
                            return (
                                <div
                                    key={id}
                                    className={`p-2 cursor-pointer rounded border-b border-gray-100 ${
                                        isSelected(id) ? 'bg-blue-100' : 'hover:bg-gray-100'
                                    } flex items-center text-sm`}
                                    onClick={() => handleItemSelectionChange(item)}
                                >
                                    {multiSelect && (
                                        <input
                                            type="checkbox"
                                            checked={isSelected(id)}
                                            readOnly
                                            className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 pointer-events-none"
                                            disabled={isDisabled}
                                        />
                                    )}
                                    <span className="ml-2 font-mono truncate" title={id}>{id}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="results-pagination">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || isDisabled}
                        className="btn-outline text-sm"
                    >
                        Previous
                    </button>
                    <span className="px-3 py-1 text-black text-sm">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || isDisabled}
                        className="btn-outline text-sm"
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

// --- Main ImageRetriever Component ---
const ImageRetriever = () => {
    // --- State Declarations ---
    const [currentStep, setCurrentStep] = useState('primary-filters');
    const [completedSteps, setCompletedSteps] = useState(['primary-filters']); // Track completed steps
    const [stepData, setStepData] = useState({
        'primary-filters': { selected: { Program: ['TCGA'] }, options: {} },
        'collections': { selected: [], options: [] },
        'patients': { selected: [], clinicalSelected: {}, patientOptions: [], clinicalOptions: {} },
        'series': { selected: [], options: [], fullData: [] }
    });

    // Loading States
    const [loading, setLoading] = useState(false);
    const [primaryFilterUpdateTimeout, setPrimaryFilterUpdateTimeout] = useState(null);

    // --- Step Management Functions ---
    const getStepStatus = (stepId) => {
        const stepIndex = WORKFLOW_STEPS.findIndex(step => step.id === stepId);
        const currentStepIndex = WORKFLOW_STEPS.findIndex(step => step.id === currentStep);

        if (completedSteps.includes(stepId) && stepIndex <= currentStepIndex) return 'completed';
        if (stepIndex === currentStepIndex) return 'active';
        if (completedSteps.includes(stepId)) return 'enabled';
        return 'disabled';
    };

    const canNavigateToStep = (stepId) => {
        const status = getStepStatus(stepId);
        return status === 'enabled' || status === 'active' || status === 'completed';
    };

    const handleStepChange = (stepId) => {
        if (canNavigateToStep(stepId)) {
            setCurrentStep(stepId);
        }
    };

    // Reset subsequent steps when a step is modified
    const resetSubsequentSteps = (fromStepId) => {
        const fromIndex = WORKFLOW_STEPS.findIndex(step => step.id === fromStepId);
        const stepsToReset = WORKFLOW_STEPS.slice(fromIndex + 1);

        setCompletedSteps(prev => prev.filter(stepId =>
            !stepsToReset.some(step => step.id === stepId)
        ));

        // Reset step data for subsequent steps
        setStepData(prev => {
            const newStepData = { ...prev };
            stepsToReset.forEach(step => {
                switch(step.id) {
                    case 'collections':
                        newStepData.collections = { selected: [], options: [] };
                        break;
                    case 'patients':
                        newStepData.patients = { selected: [], clinicalSelected: {}, patientOptions: [], clinicalOptions: {} };
                        break;
                    case 'series':
                        newStepData.series = { selected: [], options: [], fullData: [] };  // Include fullData
                        break;
                }
            });
            return newStepData;
        });
    };

    // --- API Call Functions ---
    const fetchInitialFilterOptions = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch('/craft_api/filter-options');
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();

            setStepData(prev => ({
                ...prev,
                'primary-filters': {
                    ...prev['primary-filters'],
                    options: data
                }
            }));
        } catch (error) {
            console.error('Error fetching initial filter options:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const updatePrimaryFilterOptions = useCallback(async (selectedFilters) => {
        setLoading(true);
        try {
            const filtersToSend = { ...selectedFilters };
            if (filtersToSend.Collections) {
                filtersToSend.collection_id = filtersToSend.Collections;
                delete filtersToSend.Collections;
            }

            // Get selected programs from filters
            const programs = selectedFilters.Program || ['TCGA'];

            const response = await fetch('/craft_api/filter-options', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    filters: filtersToSend,
                    programs: programs
                })
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();

            setStepData(prev => ({
                ...prev,
                'primary-filters': {
                    ...prev['primary-filters'],
                    options: { ...data, Collections: data.collection_id || [] }
                }
            }));
        } catch (error) {
            console.error('Error updating primary filter options:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchCollectionOptions = useCallback(async () => {
        setLoading(true);
        try {
            const primaryFilters = stepData['primary-filters'].selected;
            const filtersToSend = { ...primaryFilters };
            if (filtersToSend.Collections) {
                filtersToSend.collection_id = filtersToSend.Collections;
                delete filtersToSend.Collections;
            }

            // Get selected programs from filters
            const programs = primaryFilters.Program || ['TCGA'];

            const response = await fetch('/craft_api/filter-options', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    filters: filtersToSend,
                    programs: programs
                })
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();

            setStepData(prev => ({
                ...prev,
                'collections': {
                    ...prev['collections'],
                    options: data.collection_id || []
                }
            }));
        } catch (error) {
            console.error('Error fetching collection options:', error);
        } finally {
            setLoading(false);
        }
    }, [stepData]);

    const fetchPatientOptions = useCallback(async () => {
        setLoading(true);
        try {
            const primaryFilters = stepData['primary-filters'].selected;
            const selectedCollections = stepData['collections'].selected;

            const primaryFiltersToSend = { ...primaryFilters };
            delete primaryFiltersToSend.Collections;

            // Get selected programs from filters
            const programs = primaryFilters.Program || ['TCGA'];

            const response = await fetch('/craft_api/filter-patients', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    filters: primaryFiltersToSend,
                    collection_id: selectedCollections,
                    clinical_filters: {},
                    programs: programs
                })
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            const fetchedPatientIds = data.patient_ids || [];

            // Fetch clinical filter options
            const clinicalResponse = await fetch('/craft_api/clinical-filter-options', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    patient_ids: fetchedPatientIds,
                    programs: programs
                })
            });
            if (!clinicalResponse.ok) throw new Error(`HTTP error! status: ${clinicalResponse.status}`);
            const clinicalData = await clinicalResponse.json();

            setStepData(prev => ({
                ...prev,
                'patients': {
                    ...prev['patients'],
                    patientOptions: fetchedPatientIds,
                    clinicalOptions: clinicalData
                }
            }));
        } catch (error) {
            console.error('Error fetching patient options:', error);
        } finally {
            setLoading(false);
        }
    }, [stepData]);

    const updatePatientOptionsWithClinicalFilters = useCallback(async (clinicalFilters) => {
        setLoading(true);
        try {
            const primaryFilters = stepData['primary-filters'].selected;
            const selectedCollections = stepData['collections'].selected;

            const primaryFiltersToSend = { ...primaryFilters };
            delete primaryFiltersToSend.Collections;

            // Get selected programs from filters
            const programs = primaryFilters.Program || ['TCGA'];

            const response = await fetch('/craft_api/filter-patients', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    filters: primaryFiltersToSend,
                    collection_id: selectedCollections,
                    clinical_filters: clinicalFilters,
                    programs: programs
                })
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            const fetchedPatientIds = data.patient_ids || [];

            // Update clinical filter options based on new patient list
            const clinicalResponse = await fetch('/craft_api/clinical-filter-options', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    patient_ids: fetchedPatientIds,
                    programs: programs
                })
            });
            if (!clinicalResponse.ok) throw new Error(`HTTP error! status: ${clinicalResponse.status}`);
            const clinicalData = await clinicalResponse.json();

            setStepData(prev => ({
                ...prev,
                'patients': {
                    ...prev['patients'],
                    patientOptions: fetchedPatientIds,
                    clinicalOptions: clinicalData
                }
            }));
        } catch (error) {
            console.error('Error updating patient options:', error);
        } finally {
            setLoading(false);
        }
    }, [stepData]);

    const fetchSeriesOptions = useCallback(async () => {
        setLoading(true);
        try {
            const selectedPatients = stepData['patients'].selected;
            const modality = stepData['primary-filters'].selected.Modality || [];
            const programs = stepData['primary-filters'].selected.Program || ['TCGA'];

            const response = await fetch('/craft_api/series-ids-batch', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    patient_ids: selectedPatients,
                    modality: modality.join(','),
                    programs: programs
                })
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            const allSeriesData = data.series_data || [];

            // Store the raw data (with duplicates) but also create a unique series list for display
            // We'll store both: the full data and a display list
            const seriesMap = new Map();
            const fullSeriesData = [];

            allSeriesData.forEach(item => {
                fullSeriesData.push(item);

                // For display, we only want unique SeriesInstanceUID
                if (!seriesMap.has(item.SeriesInstanceUID)) {
                    // Take the first occurrence for display purposes
                    seriesMap.set(item.SeriesInstanceUID, item);
                }
            });

            setStepData(prev => ({
                ...prev,
                'series': {
                    ...prev['series'],
                    options: Array.from(seriesMap.values()), // Unique series for display
                    fullData: fullSeriesData // All data including duplicates
                }
            }));
        } catch (error) {
            console.error('Error fetching series options:', error);
        } finally {
            setLoading(false);
        }
    }, [stepData]);

    // --- Effects ---
    useEffect(() => {
        fetchInitialFilterOptions();
    }, [fetchInitialFilterOptions]);

    // --- Event Handlers ---
    const handlePrimaryFilterChange = (field, values) => {
        const newFilters = {
            ...stepData['primary-filters'].selected,
            [field]: values.map(v => v.value)
        };

        setStepData(prev => ({
            ...prev,
            'primary-filters': {
                ...prev['primary-filters'],
                selected: newFilters
            }
        }));

        // Reset subsequent steps since primary filters changed
        resetSubsequentSteps('primary-filters');

        // Debounce API call to update filter options
        if (primaryFilterUpdateTimeout) {
            clearTimeout(primaryFilterUpdateTimeout);
        }
        const timeoutId = setTimeout(() => {
            updatePrimaryFilterOptions(newFilters);
        }, 500);
        setPrimaryFilterUpdateTimeout(timeoutId);
    };

    const handleCollectionChange = (collectionId) => {
        const currentSelected = stepData['collections'].selected;
        const newSelection = currentSelected.includes(collectionId)
            ? currentSelected.filter(id => id !== collectionId)
            : [...currentSelected, collectionId];

        setStepData(prev => ({
            ...prev,
            'collections': {
                ...prev['collections'],
                selected: newSelection
            }
        }));

        // Reset subsequent steps since collections changed
        resetSubsequentSteps('collections');
    };

    const handleClinicalFilterChange = (field, value) => {
        const newClinicalFilters = { ...stepData['patients'].clinicalSelected };
        if (
            (Array.isArray(value) && value.length > 0) ||
            (!Array.isArray(value) && value !== null && value !== undefined)
        ) {
            newClinicalFilters[field] = value;
        } else {
            delete newClinicalFilters[field];
        }

        setStepData(prev => ({
            ...prev,
            'patients': {
                ...prev['patients'],
                clinicalSelected: newClinicalFilters
            }
        }));

        // Update patient options based on clinical filters
        updatePatientOptionsWithClinicalFilters(newClinicalFilters);

        // Reset subsequent steps since clinical filters changed
        resetSubsequentSteps('patients');
    };

    const handlePatientChange = (patientId) => {
        const currentSelected = stepData['patients'].selected;
        const newSelection = currentSelected.includes(patientId)
            ? currentSelected.filter(pid => pid !== patientId)
            : [...currentSelected, patientId];

        setStepData(prev => ({
            ...prev,
            'patients': {
                ...prev['patients'],
                selected: newSelection
            }
        }));

        // Reset subsequent steps since patients changed
        resetSubsequentSteps('patients');
    };

    const handleSeriesChange = (seriesItem) => {
        const seriesId = seriesItem.SeriesInstanceUID;
        const currentSelected = stepData['series'].selected;
        const newSelection = currentSelected.includes(seriesId)
            ? currentSelected.filter(id => id !== seriesId)
            : [...currentSelected, seriesId];

        setStepData(prev => ({
            ...prev,
            'series': {
                ...prev['series'],
                selected: newSelection
            }
        }));
    };

    // --- Next Step Handlers ---
    const handleNextToCollections = async () => {
        await fetchCollectionOptions();
        setCompletedSteps(prev => [...new Set([...prev, 'collections'])]);
        setCurrentStep('collections');
    };

    const handleNextToPatients = async () => {
        await fetchPatientOptions();
        setCompletedSteps(prev => [...new Set([...prev, 'patients'])]);
        setCurrentStep('patients');
    };

    const handleNextToSeries = async () => {
        await fetchSeriesOptions();
        setCompletedSteps(prev => [...new Set([...prev, 'series'])]);
        setCurrentStep('series');
    };

    const handleSubmitSelection = () => {
        // Get all selected SeriesInstanceUIDs
        const selectedSeriesIds = stepData['series'].selected;

        // Get the full data (including duplicates)
        const fullData = stepData['series'].fullData || [];

        // Create a map where key is SeriesInstanceUID and value is array of all records
        const seriesGroupedData = {};

        fullData.forEach(item => {
            if (selectedSeriesIds.includes(item.SeriesInstanceUID)) {
                if (!seriesGroupedData[item.SeriesInstanceUID]) {
                    seriesGroupedData[item.SeriesInstanceUID] = [];
                }
                seriesGroupedData[item.SeriesInstanceUID].push(item);
            }
        });

        // Create the original param structure for backward compatibility
        const selectedSeries = stepData['series'].options.filter(item =>
            stepData['series'].selected.includes(item.SeriesInstanceUID)
        );

        const param = {
            data_source_type: 'IDC',
            primary_filters: stepData['primary-filters'].selected,
            collections: stepData['collections'].selected,
            clinical_filters: stepData['patients'].clinicalSelected,
            patient_ids: stepData['patients'].selected,
            series_grouped_data: seriesGroupedData  // New format: grouped by SeriesInstanceUID
        };


        if (window.opener && window.opener.getIDRParameter) {
            window.opener.getIDRParameter(param);
        } else {
            console.log('Full param structure:', param);
        }
    };

    // --- Helper Functions ---
    const getPrimarySelectOptions = (field, options) => {
        if (!Array.isArray(options)) return [];
        return options.map(opt => ({
            value: opt,
            label: VALUE_MAPPINGS[field]?.[opt] || opt || '(empty)'
        })).sort((a, b) => a.label.localeCompare(b.label));
    };

    const handleSelectAll = (sectionType, visibleIds) => {
        switch(sectionType) {
            case 'Collections':
                const currentCollections = stepData['collections'].selected;
                const newCollections = [...new Set([...currentCollections, ...visibleIds])];
                setStepData(prev => ({
                    ...prev,
                    'collections': { ...prev['collections'], selected: newCollections }
                }));
                resetSubsequentSteps('collections');
                break;
            case 'Patients':
                const currentPatients = stepData['patients'].selected;
                const newPatients = [...new Set([...currentPatients, ...visibleIds])];
                setStepData(prev => ({
                    ...prev,
                    'patients': { ...prev['patients'], selected: newPatients }
                }));
                resetSubsequentSteps('patients');
                break;
            case 'Series':
                const currentSeries = stepData['series'].selected;
                const newSeries = [...new Set([...currentSeries, ...visibleIds])];
                setStepData(prev => ({
                    ...prev,
                    'series': { ...prev['series'], selected: newSeries }
                }));
                break;
        }
    };

    const handleSelectNone = (sectionType, visibleIds) => {
        const visibleIdsSet = new Set(visibleIds);
        switch(sectionType) {
            case 'Collections':
                const newCollections = stepData['collections'].selected.filter(id => !visibleIdsSet.has(id));
                setStepData(prev => ({
                    ...prev,
                    'collections': { ...prev['collections'], selected: newCollections }
                }));
                resetSubsequentSteps('collections');
                break;
            case 'Patients':
                const newPatients = stepData['patients'].selected.filter(id => !visibleIdsSet.has(id));
                setStepData(prev => ({
                    ...prev,
                    'patients': { ...prev['patients'], selected: newPatients }
                }));
                resetSubsequentSteps('patients');
                break;
            case 'Series':
                const newSeries = stepData['series'].selected.filter(id => !visibleIdsSet.has(id));
                setStepData(prev => ({
                    ...prev,
                    'series': { ...prev['series'], selected: newSeries }
                }));
                break;
        }
    };

    // --- Step Content Rendering Functions ---
    const renderPrimaryFiltersStep = () => (
        <div className="step-content-card">
            <div className="step-content-header">
                <h2 className="step-content-title">Primary Filters</h2>
                <p className="step-content-description">
                    Configure basic image metadata filters to narrow down available collections
                </p>
            </div>
            <div className="step-content-body">
                <div className="filter-section">
                    <h3 className="filter-section-title">Filter Options</h3>
                    <div className="filter-grid">
                        {Object.entries(stepData['primary-filters'].options)
                            .filter(([field]) => field !== 'collection_id')
                            .map(([field, options]) => (
                                <div key={field} className="filter-item">
                                    <label className="filter-label">
                                        {FIELD_TITLES[field] || field}:
                                        {Array.isArray(options) ? ` (${options.length})` : ''}
                                    </label>
                                    <Select
                                        isMulti
                                        options={getPrimarySelectOptions(field, options)}
                                        value={getPrimarySelectOptions(field, stepData['primary-filters'].selected[field] || [])}
                                        onChange={(selected) => handlePrimaryFilterChange(field, selected || [])}
                                        isDisabled={loading}
                                        closeMenuOnSelect={false}
                                        placeholder={`Select ${FIELD_TITLES[field] || field}...`}
                                        styles={{ menu: base => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </div>
                            ))}
                    </div>
                </div>

                {/* Selection Summary */}
                {Object.keys(stepData['primary-filters'].selected).length > 0 && (
                    <div className="selection-summary">
                        <h4 className="selection-summary-title">Current Selection</h4>
                        <div className="selection-summary-content">
                            {Object.entries(stepData['primary-filters'].selected).map(([field, values]) => (
                                <div key={field} className="mb-1">
                                    <strong>{FIELD_TITLES[field] || field}:</strong>
                                    {values.map(value => (
                                        <span key={value} className="selection-tag">
                                            {VALUE_MAPPINGS[field]?.[value] || value}
                                        </span>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="step-actions">
                    <div className="step-actions-left"></div>
                    <div className="step-actions-right">
                        <button
                            className="btn-primary"
                            onClick={handleNextToCollections}
                            disabled={Object.keys(stepData['primary-filters'].selected).length === 0 || loading}
                        >
                            Next: Select Collections
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderCollectionsStep = () => (
        <div className="step-content-card">
            <div className="step-content-header">
                <h2 className="step-content-title">Collections</h2>
                <p className="step-content-description">
                    Select data collections based on your primary filters
                </p>
            </div>
            <div className="step-content-body">
                <PaginatedSection
                    title={FIELD_TITLES.Collections}
                    items={stepData['collections'].options}
                    selectedItems={stepData['collections'].selected}
                    onItemSelect={handleCollectionChange}
                    onSelectAll={(visibleIds) => handleSelectAll('Collections', visibleIds)}
                    onSelectNone={(visibleIds) => handleSelectNone('Collections', visibleIds)}
                    searchPlaceholder="Search collections..."
                    multiSelect={true}
                    isDisabled={loading}
                />

                <div className="step-actions">
                    <div className="step-actions-left">
                        <button
                            className="btn-secondary"
                            onClick={() => handleStepChange('primary-filters')}
                        >
                            Back: Primary Filters
                        </button>
                    </div>
                    <div className="step-actions-right">
                        <button
                            className="btn-primary"
                            onClick={handleNextToPatients}
                            disabled={stepData['collections'].selected.length === 0 || loading}
                        >
                            Next: Select Patients
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderPatientsStep = () => (
        <div className="step-content-card">
            <div className="step-content-header">
                <h2 className="step-content-title">Patient IDs & Clinical Filters</h2>
                <p className="step-content-description">
                    Filter patients using clinical criteria and select specific patient IDs
                </p>
            </div>
            <div className="step-content-body">
                <ClinicalFilters
                    options={stepData['patients'].clinicalOptions.options || {}}
                    selectedFilters={stepData['patients'].clinicalSelected}
                    onChange={handleClinicalFilterChange}
                    loading={loading}
                    patientCount={stepData['patients'].clinicalOptions.total_patient_count || 0}
                />

                <PaginatedSection
                    title="Patient IDs"
                    items={stepData['patients'].patientOptions}
                    selectedItems={stepData['patients'].selected}
                    onItemSelect={handlePatientChange}
                    onSelectAll={(visibleIds) => handleSelectAll('Patients', visibleIds)}
                    onSelectNone={(visibleIds) => handleSelectNone('Patients', visibleIds)}
                    searchPlaceholder="Search patient IDs..."
                    multiSelect={true}
                    isDisabled={loading}
                />

                <div className="step-actions">
                    <div className="step-actions-left">
                        <button
                            className="btn-secondary"
                            onClick={() => handleStepChange('collections')}
                        >
                            Back: Collections
                        </button>
                    </div>
                    <div className="step-actions-right">
                        <button
                            className="btn-primary"
                            onClick={handleNextToSeries}
                            disabled={stepData['patients'].selected.length === 0 || loading}
                        >
                            Next: Select Series
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );


    const renderSeriesStep = () => (
        <div className="step-content-card">
            <div className="step-content-header">
                <h2 className="step-content-title">Series IDs</h2>
                <p className="step-content-description">
                    Select specific image series for submission
                </p>
            </div>
            <div className="step-content-body">
                <PaginatedSection
                    title="Series IDs"
                    items={stepData['series'].options}
                    selectedItems={stepData['series'].selected}
                    onItemSelect={handleSeriesChange}
                    onSelectAll={(visibleIds) => handleSelectAll('Series', visibleIds)}
                    onSelectNone={(visibleIds) => handleSelectNone('Series', visibleIds)}
                    searchPlaceholder="Search series Instance UID..."
                    multiSelect={true}
                    isDisabled={loading}
                    stepData={stepData}  // Pass stepData to access fullData
                />

                <div className="step-actions">
                    <div className="step-actions-left">
                        <button
                            className="btn-secondary"
                            onClick={() => handleStepChange('patients')}
                        >
                            Back: Patients
                        </button>
                    </div>
                    <div className="step-actions-right">
                        <button
                            className="btn-primary"
                            disabled={stepData['series'].selected.length === 0}
                            onClick={handleSubmitSelection}
                        >
                            Submit Selection ({stepData['series'].selected.length})
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderCurrentStep = () => {
        switch (currentStep) {
            case 'primary-filters':
                return renderPrimaryFiltersStep();
            case 'collections':
                return renderCollectionsStep();
            case 'patients':
                return renderPatientsStep();
            case 'series':
                return renderSeriesStep();
            default:
                return renderPrimaryFiltersStep();
        }
    };

    // --- Main Render ---
    return (
        <div style={containerStyle}>
            <LoadingOverlay isLoading={loading} />

            <div className="main-app-layout">
                {/* Left Sidebar Navigation */}
                <nav className="step-sidebar">
                    <div className="step-sidebar-header">
                        <h1 className="step-sidebar-title">Image Retrieval</h1>
                    </div>

                    <ul className="step-navigation">
                        {WORKFLOW_STEPS.map((step, index) => {
                            const status = getStepStatus(step.id);
                            return (
                                <li key={step.id} className="step-nav-item">
                                    <button
                                        className={`step-nav-button ${status}`}
                                        onClick={() => handleStepChange(step.id)}
                                        disabled={status === 'disabled'}
                                    >
                                        <span className="step-indicator">{index + 1}</span>
                                        <span>{step.title}</span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </nav>

                {/* Main Content Area */}
                <main className="step-content-area">
                    {renderCurrentStep()}
                </main>
            </div>
        </div>
    );
};

export default ImageRetriever;
